import { gql, useQuery } from '@apollo/client';
import { useState } from 'react';
import { LoadingErrorWrapper } from '@/admins/SmartReports/components/Components/Common/LoadingErrorWrapper';
import type {
  ComponentDefinition,
  ComponentEditProps,
  ComponentViewProps,
  UseDataType,
} from '@/admins/SmartReports/components/Components/types';
import { SmartReportComponentTypesEnum, SmartReportTypeEnum } from '@/admins/SmartReports/types';

export const COMPONENT_ID = 'country_view_picture';

// we need the ID for Apollo to cache properly
const QUERY = gql`
  query assetV2($input: EntityIdentifierInput!) {
    assetV2(input: $input) {
      id
      countryViewPictureUrl
    }
  }
`;

const useData = ({ portfolio, onComponentLoad }: UseDataType<string>) => {
  const [dataFetched, setDataFetched] = useState(null);
  const assetId = portfolio?.assets?.[0];
  const { loading, error } = useQuery(QUERY, {
    variables: { input: { id: assetId } },
    // skip if there's no assetId or if the data is already loaded
    skip: !assetId || !!dataFetched,
    onCompleted: data => {
      if (data?.assetV2?.countryViewPictureUrl) {
        setDataFetched(data?.assetV2?.countryViewPictureUrl);
        if (onComponentLoad) {
          onComponentLoad({
            componentId: COMPONENT_ID,
            // Since we fetch a presigned url that expires in 15m, we can't save the url to the report itself
            // So we save null to data to make the component non-persistent
            data: null,
          });
        }
      }
    },
  });

  return { data: dataFetched, error, loading };
};

const CountryViewPicture = ({ portfolio, onComponentLoad }: ComponentEditProps<string>) => {
  const { data: url, loading, error } = useData({ portfolio, onComponentLoad });

  return (
    <LoadingErrorWrapper loading={loading} error={error} data={url}>
      <img
        style={{ width: '100%', height: 'auto', objectFit: 'cover' }}
        alt='country_view_picture'
        src={url || ''}
      />
    </LoadingErrorWrapper>
  );
};

const CountryViewPictureView = ({ portfolio }: ComponentViewProps<string>) => {
  return (
    <div style={{ display: 'flex', height: '100%', width: '100%' }}>
      {/* Since this component is not persistent, there is no need for a separate view component */}
      <CountryViewPicture portfolio={portfolio || {}} />
    </div>
  );
};

const CountryViewPictureThumbnail = ({ portfolio }: ComponentEditProps<string>) => {
  return (
    <div style={{ width: 'auto' }}>
      {/* Since this component is not persistent, there is no need for a separate view component */}
      <CountryViewPicture portfolio={portfolio || {}} />
    </div>
  );
};

export const CountryViewPictureComponent: ComponentDefinition<string> = {
  id: COMPONENT_ID,
  name: 'Country View',
  type: SmartReportComponentTypesEnum.Image,
  reportTypes: [SmartReportTypeEnum.Asset],
  Thumbnail: CountryViewPictureThumbnail,
  Component: CountryViewPicture,
  ComponentView: CountryViewPictureView,
};
